import Dexie, { type EntityTable, type Table } from 'dexie'
import type { AnswerRef, Attachment, ChecklistReasonInput, ChannelSummary, MemberQuiet, MemberRole, MemberSource, Message, MessageContext, User } from '../api/types'
import { needsMediaDefaults, withAttachmentDefaults } from '../lib/media'
import { canPostIn } from '../lib/posting'

/**
 * Local channel row: the server summary plus client-only fields.
 * `version` is null when the channel must be fetched as a snapshot (admin view, force resync).
 */
export interface LocalChannel extends Omit<ChannelSummary, 'version'> {
  version: string | null
  /** Last time this device opened the channel. */
  lastViewedAt: string | null
  /** Whether older history exists before the oldest locally stored message. */
  hasMore: boolean
  /**
   * Hash of the server summary this row was written from, sent back with the next sync so an unchanged conversation is
   * answered "unchanged". Any other write to the row clears it (the updating hook below), so a row that drifted from its
   * summary always gets the summary again.
   */
  summaryHash?: string | null
}

export interface MemberRow {
  channelId: string
  userId: string
  role: MemberRole
  /** "managed": added by an integration, which alone can remove it. */
  source: MemberSource
  /** What this conversation will not do for them, as it stands: everyone in it is shown this. */
  quiet?: MemberQuiet
}

export type OutboxState = 'queued' | 'sending' | 'failed'

/**
 * Attachment metadata kept with an outbox row so the pending message can render. Rows queued before video and audio
 * existed lack the media fields.
 */
export type OutboxAttachment = Pick<Attachment, 'id' | 'fileName' | 'contentType' | 'sizeBytes' | 'width' | 'height'> &
  Partial<Pick<Attachment, 'kind' | 'voice' | 'durationMs' | 'waveform'>>

export interface OutboxRow {
  /** Client-generated UUIDv7, reused as the message id. */
  id: string
  channelId: string
  body: string
  attachmentIds: string[]
  attachments: OutboxAttachment[]
  replyToId: string | null
  /** A private reply: the channel message it answers. Absent from rows queued before private replies existed. */
  context?: MessageContext | null
  /** An answer to a bot's question. Absent from rows queued before questions existed. */
  answer?: AnswerRef | null
  /** A checklist's Done: the reasons sent with it. Absent from rows queued before checklists existed. */
  reasons?: ChecklistReasonInput[] | null
  createdAt: string
  attempts: number
  state: OutboxState
  /** Epoch ms before which the sender must not retry. */
  nextAttemptAt: number
  /** Last error shown in the UI for failed rows. */
  error: string | null
  /** Set when the server accepted the message but the event could not be applied yet (gap). */
  confirmedSeq: number | null
}

export interface MetaRow {
  key: string
  value: unknown
}

export class ChatDatabase extends Dexie {
  channels!: EntityTable<LocalChannel, 'id'>
  messages!: EntityTable<Message, 'id'>
  members!: Table<MemberRow, [string, string]>
  users!: EntityTable<User, 'id'>
  outbox!: EntityTable<OutboxRow, 'id'>
  meta!: EntityTable<MetaRow, 'key'>

  constructor(name = 'boss-chat') {
    super(name)
    this.version(1).stores({
      channels: 'id, type',
      messages: 'id, channelId, [channelId+seq]',
      members: '[channelId+userId], channelId, userId',
      users: 'id',
      outbox: 'id, channelId, state, createdAt',
      meta: 'key',
    })
    // Rows cached before managed channels, bots, restricted channels or video and audio existed lack the new fields.
    // Fill the defaults whenever a row is read (get, bulkGet, collections and live queries), so the rest of the app can
    // trust the types without a schema upgrade: no new index is needed.
    this.channels.hook('reading', withChannelDefaults)
    this.messages.hook('reading', withMessageDefaults)
    this.members.hook('reading', withMemberDefaults)
    this.users.hook('reading', withUserDefaults)
    this.channels.hook('updating', (modifications) => ('summaryHash' in modifications ? undefined : { summaryHash: null }))
  }
}

// Reading hooks also run for misses (`get` of an unknown key), so they must pass undefined through.

export function withChannelDefaults(row: LocalChannel | undefined): LocalChannel | undefined {
  if (
    !row ||
    (row.managed !== undefined &&
      row.bot !== undefined &&
      row.posting !== undefined &&
      row.replyBot !== undefined &&
      row.canPost !== undefined &&
      row.memberCount !== undefined &&
      (!row.lastMessage || row.lastMessage.media !== undefined))
  ) {
    return row
  }
  const filled = {
    ...row,
    managed: row.managed ?? null,
    bot: row.bot ?? null,
    posting: row.posting ?? 'everyone',
    replyBot: row.replyBot ?? null,
    memberCount: row.memberCount ?? row.memberIds.length,
    lastMessage: row.lastMessage ? { ...row.lastMessage, media: row.lastMessage.media ?? null } : null,
  }
  return { ...filled, canPost: row.canPost ?? canPostIn(filled) }
}

export function withMessageDefaults(row: Message | undefined): Message | undefined {
  if (!row || !row.attachments.some(needsMediaDefaults)) return row
  return { ...row, attachments: row.attachments.map(withAttachmentDefaults) }
}

export function withMemberDefaults(row: MemberRow | undefined): MemberRow | undefined {
  if (!row) return row
  if (row.source !== undefined && row.quiet !== undefined) return row
  return { ...row, source: row.source ?? 'manual', quiet: row.quiet ?? null }
}

export function withUserDefaults<T extends User>(user: T | undefined): T | undefined {
  if (!user || (user.kind !== undefined && user.linked !== undefined && user.description !== undefined)) return user
  return { ...user, kind: user.kind ?? 'person', linked: user.linked ?? true, description: user.description ?? null }
}

export const db = new ChatDatabase()

/** Every table that holds channel data, for multi-table transactions. */
export function channelTables() {
  return [db.channels, db.messages, db.members, db.outbox] as const
}

/** Delete a channel and everything stored for it. Call inside a transaction including channelTables(). */
export async function deleteChannelData(channelId: string): Promise<void> {
  await db.messages.where('channelId').equals(channelId).delete()
  await db.members.where('channelId').equals(channelId).delete()
  await db.outbox.where('channelId').equals(channelId).delete()
  await db.channels.delete(channelId)
}

/** Messages for one channel ordered by seq. */
export function channelMessages(channelId: string) {
  return db.messages.where('[channelId+seq]').between([channelId, Dexie.minKey], [channelId, Dexie.maxKey])
}

export async function clearLocalData(): Promise<void> {
  await db.transaction('rw', [db.channels, db.messages, db.members, db.users, db.outbox, db.meta], async () => {
    await Promise.all([
      db.channels.clear(),
      db.messages.clear(),
      db.members.clear(),
      db.users.clear(),
      db.outbox.clear(),
      db.meta.clear(),
    ])
  })
}
